import type {
  ClusterUpdate,
  KafkaExtensionConfiguration,
  KinesisExtensionConfiguration,
} from '@/generated/http-clients/v3ApiSchemas'
import type React from 'react'
import { createContext, useContext, useMemo, useState } from 'react'

// ClusterUpdate variant where product_id is optional (for form state before selection)
export type ClusterUpdateWithOptionalProductId = Omit<
  ClusterUpdate,
  'product_id'
> & {
  product_id?: number
}

export type ClusterUpdateFormData = ClusterUpdateWithOptionalProductId & {
  // these values below are here to be used in a form and they will be mapped
  // to where they need to be (nested in the structure of ClusterUpdate) later
  // template_id and changelog do not need to be replicated here as they're
  // already at the root of ClusterUpdate
  _organizationId: string
  _deploymentLabel: string | null
  _clusterName: string
  _kafkaExtension?: KafkaExtensionConfiguration
  _kinesisExtension?: KinesisExtensionConfiguration
  _overrideYaml?: string
}

interface ClusterConfigRequestContextType {
  request: ClusterUpdateFormData
  onChange: <Key extends keyof ClusterUpdateFormData>(
    name: Key,
    value: ClusterUpdateFormData[Key],
  ) => void
  errors: Record<string, string>
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>
}

const ClusterConfigRequestContext = createContext<
  ClusterConfigRequestContextType | undefined
>(undefined)

export const ClusterConfigRequestProvider: React.FC<{
  children: React.ReactNode
}> = ({ children }) => {
  const [request, setRequest] = useState<ClusterUpdateFormData>({
    system: {
      global: {
        name: '',
        deployment_label: '',
      },
    },
    override: {},
    customer: {
      cluster: {},
      extensions: {},
    },
    template_id: '',
    product_id: undefined,
    changelog: '',
    _organizationId: '',
    _deploymentLabel: null,
    _clusterName: '',
    _overrideYaml: '',
  })

  const [errors, setErrors] = useState<Record<string, string>>({})

  function onChange<Key extends keyof ClusterUpdateFormData>(
    name: Key,
    value: ClusterUpdateFormData[Key],
  ) {
    setRequest((prevState) => ({ ...prevState, [name]: value }))
  }

  // Memoize the context value so it only changes when one of its fields changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: onChange should not be a dependency
  const value = useMemo(
    () => ({
      request,
      onChange,
      errors,
      setErrors,
    }),
    [request, errors, setErrors],
  )

  return (
    <ClusterConfigRequestContext.Provider value={value}>
      {children}
    </ClusterConfigRequestContext.Provider>
  )
}

export const useClusterConfigRequest = () => {
  const context = useContext(ClusterConfigRequestContext)
  if (!context) {
    throw new Error('useRequest must be used within a RequestProvider')
  }
  return context
}
